"use client"

import { useEffect, useId, useState } from "react"
import { TOKENOMICS_DISTRIBUTION } from "@/lib/dapp-content"

export type ReactorCoreStageProps = {
  active: number | null
  setActive: (i: number | null) => void
  /** Extra classes on the outer `.tk-canvas` wrapper (e.g. hero sizing). */
  className?: string
}

export function ReactorCoreStage({ active, setActive, className }: ReactorCoreStageProps) {
  const [tick, setTick] = useState(0)
  const rawId = useId()
  const gradId = `reactorCore-${rawId.replace(/[^a-zA-Z0-9_-]/g, "")}`

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 120)
    return () => clearInterval(id)
  }, [])

  const W = 600
  const H = 540
  const cx = W / 2
  const cy = H / 2
  let cur = -Math.PI / 2
  const total = TOKENOMICS_DISTRIBUTION.reduce((s, t) => s + t.value, 0)
  const slices = TOKENOMICS_DISTRIBUTION.map((t, i) => {
    const start = cur
    const ang = (t.value / total) * Math.PI * 2
    const end = start + ang
    cur = end
    return { start, end, mid: (start + end) / 2, t, i }
  })

  const arcPath = (start: number, end: number, r: number) => {
    const sx = cx + Math.cos(start) * r
    const sy = cy + Math.sin(start) * r
    const ex = cx + Math.cos(end) * r
    const ey = cy + Math.sin(end) * r
    const large = end - start > Math.PI ? 1 : 0
    return `M ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey}`
  }

  const colorFor = (idx: number) =>
    ["#8fbfa8", "#00e5ff", "#c9a96a", "#a3d4bd", "#6e9784", "#5fc7e0", "#b39870", "#7fa896"][idx % 8]!

  const m = active != null ? TOKENOMICS_DISTRIBUTION[active] : null
  const rootClass = ["tk-canvas", className].filter(Boolean).join(" ")

  return (
    <div className={rootClass}>
      <div className="tk-stamp">
        <span className="lit">◆</span> REACTOR.CORE · 1B SUPPLY
      </div>
      <div className="tk-stamp-r">
        JETTON · TEP-74
        <br />
        TON_TESTNET
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="tk-canvas-svg" aria-hidden>
        <defs>
          <radialGradient id={gradId} cx="50%" cy="50%">
            <stop offset="0%" stopColor="rgba(143,191,168,0.5)" />
            <stop offset="60%" stopColor="rgba(143,191,168,0.08)" />
            <stop offset="100%" stopColor="rgba(143,191,168,0)" />
          </radialGradient>
        </defs>
        {[200, 175, 150].map((r, k) => (
          <circle
            key={`r${k}`}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="var(--line-2)"
            strokeOpacity={k === 0 ? 0.6 : 0.3}
            strokeDasharray={k === 0 ? "0" : "3 6"}
            strokeWidth={k === 0 ? 0.8 : 0.4}
          />
        ))}
        {Array.from({ length: 60 }).map((_, i) => {
          const a = (i / 60) * Math.PI * 2 - Math.PI / 2
          const r1 = 200
          const r2 = i % 5 === 0 ? 188 : 194
          return (
            <line
              key={`tk${i}`}
              x1={cx + Math.cos(a) * r1}
              y1={cy + Math.sin(a) * r1}
              x2={cx + Math.cos(a) * r2}
              y2={cy + Math.sin(a) * r2}
              stroke="var(--text-faint)"
              strokeOpacity="0.4"
              strokeWidth="0.5"
            />
          )
        })}
        {slices.map((s) => {
          const isA = s.i === active
          const muted = active != null && !isA
          const r = isA ? 178 : 165
          return (
            <g key={`s${s.i}`} onMouseEnter={() => setActive(s.i)} style={{ cursor: "crosshair" }}>
              <path
                d={arcPath(s.start + 0.012, s.end - 0.012, r)}
                fill="none"
                stroke={colorFor(s.i)}
                strokeOpacity={muted ? 0.18 : isA ? 0.95 : 0.55}
                strokeWidth={isA ? 32 : 22}
                style={{ transition: "all .25s" }}
              />
              <path d={arcPath(s.start, s.end, 165)} fill="none" stroke="transparent" strokeWidth="50" />
              <circle cx={cx + Math.cos(s.mid) * 198} cy={cy + Math.sin(s.mid) * 198} r={isA ? 3 : 1.5} fill={colorFor(s.i)} />
              {isA && (
                <>
                  <line
                    x1={cx + Math.cos(s.mid) * 200}
                    y1={cy + Math.sin(s.mid) * 200}
                    x2={cx + Math.cos(s.mid) * 230}
                    y2={cy + Math.sin(s.mid) * 230}
                    stroke={colorFor(s.i)}
                    strokeWidth="0.8"
                    strokeDasharray="2 3"
                  />
                  <text
                    x={cx + Math.cos(s.mid) * 240}
                    y={cy + Math.sin(s.mid) * 240 + 3}
                    textAnchor={Math.cos(s.mid) > 0 ? "start" : "end"}
                    fill={colorFor(s.i)}
                    fontSize="9"
                    fontFamily="var(--mono)"
                    letterSpacing="2.5"
                    fontWeight="600"
                  >
                    {s.t.name.toUpperCase()}
                  </text>
                </>
              )}
            </g>
          )
        })}
        <circle cx={cx} cy={cy} r="80" fill={`url(#${gradId})`} />
        <circle cx={cx} cy={cy} r="64" fill="none" stroke="var(--green-bright)" strokeOpacity="0.4" strokeDasharray="2 4" />
        <circle cx={cx} cy={cy} r={50 + Math.sin(tick * 0.04) * 2} fill="var(--bg-1)" stroke="var(--green-bright)" strokeWidth="1" />
        <text x={cx} y={cy - 6} textAnchor="middle" fill="var(--text)" fontSize="32" fontFamily="var(--display)" fontWeight="700" letterSpacing="-0.02em">
          {m ? `${m.value}%` : "1B"}
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle" fill="var(--green-bright)" fontSize="9" fontFamily="var(--mono)" letterSpacing="3">
          {m ? m.name.toUpperCase() : "TOTAL_SUPPLY"}
        </text>
        <text x={cx} y={cy + 30} textAnchor="middle" fill="var(--text-faint)" fontSize="8" fontFamily="var(--mono)" letterSpacing="2">
          {m ? `// ${m.tokens} RIVYU` : "// RIV · TEP-74"}
        </text>
        <g style={{ transformOrigin: `${cx}px ${cy}px`, transform: `rotate(${tick * 0.2}deg)` }}>
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i / 8) * Math.PI * 2
            return <circle key={`od${i}`} cx={cx + Math.cos(a) * 220} cy={cy + Math.sin(a) * 220} r="2" fill="var(--green-bright)" fillOpacity="0.5" />
          })}
        </g>
      </svg>
    </div>
  )
}
